// ApiUsageManager.js
// Ce module gère l'utilisation des API, les quotas, et la mise en cache des résultats

/**
 * Classe qui gère l'utilisation des API, les limitations et le cache
 */
class ApiUsageManager {
  constructor() {
    this.loadUsageData();
    this.resultCache = new Map();
  }

  /**
   * Charge les données d'utilisation depuis le localStorage
   */
  loadUsageData() {
    try {
      const storedData = localStorage.getItem('artworkApiUsage');
      this.usage = storedData ? JSON.parse(storedData) : {
        currentMonth: new Date().getMonth(),
        currentYear: new Date().getFullYear(),
        callCount: 0,
        isPremium: false,
        lastReset: new Date().toISOString(),
        cacheHits: 0
      };
      
      // Réinitialiser le compteur si on a changé de mois
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      if (currentMonth !== this.usage.currentMonth || currentYear !== this.usage.currentYear) {
        this.resetMonthlyUsage();
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données d\'utilisation API:', error);
      this.resetUsageData();
    }
  }

  /**
   * Sauvegarde les données d'utilisation dans le localStorage
   */
  saveUsageData() {
    try {
      localStorage.setItem('artworkApiUsage', JSON.stringify(this.usage));
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des données d\'utilisation API:', error);
    }
  }

  /**
   * Réinitialise les données d'utilisation
   */
  resetUsageData() {
    this.usage = {
      currentMonth: new Date().getMonth(),
      currentYear: new Date().getFullYear(),
      callCount: 0,
      isPremium: false,
      lastReset: new Date().toISOString(),
      cacheHits: 0
    };
    this.saveUsageData();
  }

  /**
   * Réinitialise le compteur mensuel
   */
  resetMonthlyUsage() {
    this.usage.currentMonth = new Date().getMonth();
    this.usage.currentYear = new Date().getFullYear();
    this.usage.callCount = 0;
    this.usage.lastReset = new Date().toISOString();
    this.saveUsageData();
  }

  /**
   * Vérifie si l'utilisateur a atteint sa limite d'appels API pour le mois
   * @returns {boolean} true si l'utilisateur peut faire un appel API, false sinon
   */
  canMakeApiCall() {
    // Les utilisateurs premium ont droit à plus d'appels
    const monthlyLimit = this.usage.isPremium ? 10 : 5;
    return this.usage.callCount < monthlyLimit;
  }

  /**
   * Récupère le nombre d'appels API restants pour le mois
   * @returns {number} nombre d'appels restants
   */
  getRemainingCalls() {
    const monthlyLimit = this.usage.isPremium ? 10 : 5;
    return Math.max(0, monthlyLimit - this.usage.callCount);
  }

  /**
   * Génère une clé de cache unique basée sur la description
   * @param {string} description - Description de l'œuvre d'art
   * @returns {string} Clé de cache
   */
  generateCacheKey(description) {
    // Normaliser la description pour le cache (supprimer espaces en trop, mettre en minuscules)
    const normalizedDesc = description.trim().toLowerCase().replace(/\s+/g, ' ');
    
    // Utiliser les 100 premiers caractères comme base de la clé
    // puis ajouter un hash simple pour le reste de la description
    const baseKey = normalizedDesc.substring(0, 100);
    
    // Calcul d'un hash simple pour le reste du texte
    let hash = 0;
    for (let i = 100; i < normalizedDesc.length; i++) {
      hash = ((hash << 5) - hash) + normalizedDesc.charCodeAt(i);
      hash |= 0; // Conversion en entier 32 bits
    }
    
    return `${baseKey}_${hash}`;
  }

  /**
   * Vérifie si un résultat est dans le cache
   * @param {string} description - Description de l'œuvre d'art
   * @returns {Object|null} Résultat mis en cache ou null si pas trouvé
   */
  getCachedResult(description) {
    const cacheKey = this.generateCacheKey(description);
    const cachedItem = this.resultCache.get(cacheKey);
    
    if (cachedItem) {
      // Mettre à jour les statistiques de cache
      this.usage.cacheHits++;
      this.saveUsageData();
      return cachedItem.result;
    }
    
    return null;
  }

  /**
   * Met en cache un résultat d'analyse
   * @param {string} description - Description de l'œuvre d'art
   * @param {Object} result - Résultat de l'analyse
   */
  cacheResult(description, result) {
    const cacheKey = this.generateCacheKey(description);
    
    // Mettre en cache avec horodatage
    this.resultCache.set(cacheKey, {
      result,
      timestamp: new Date().getTime()
    });
    
    // Limiter la taille du cache à 100 éléments
    if (this.resultCache.size > 100) {
      // Supprimer l'élément le plus ancien
      let oldestKey = null;
      let oldestTime = Infinity;
      
      for (const [key, value] of this.resultCache.entries()) {
        if (value.timestamp < oldestTime) {
          oldestTime = value.timestamp;
          oldestKey = key;
        }
      }
      
      if (oldestKey) {
        this.resultCache.delete(oldestKey);
      }
    }
  }

  /**
   * Incrémente le compteur d'utilisation API
   */
  incrementUsageCount() {
    this.usage.callCount++;
    this.saveUsageData();
  }

  /**
   * Active le compte premium
   */
  activatePremium() {
    this.usage.isPremium = true;
    this.saveUsageData();
  }

  /**
   * Désactive le compte premium
   */
  deactivatePremium() {
    this.usage.isPremium = false;
    this.saveUsageData();
  }

  /**
   * Vérifie si l'utilisateur a un compte premium
   * @returns {boolean} true si l'utilisateur a un compte premium
   */
  isPremium() {
    return this.usage.isPremium;
  }

  /**
   * Obtient les statistiques d'utilisation
   * @returns {Object} statistiques d'utilisation
   */
  getUsageStats() {
    return {
      callCount: this.usage.callCount,
      remainingCalls: this.getRemainingCalls(),
      isPremium: this.usage.isPremium,
      cacheHits: this.usage.cacheHits,
      lastReset: this.usage.lastReset,
      nextReset: this.getNextResetDate()
    };
  }

  /**
   * Calcule la date de la prochaine réinitialisation mensuelle
   * @returns {string} date au format ISO
   */
  getNextResetDate() {
    const now = new Date();
    // Obtenir le premier jour du mois prochain
    const nextReset = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return nextReset.toISOString();
  }
  
  /**
   * Efface le cache de résultats
   */
  clearCache() {
    this.resultCache.clear();
  }
}

// Exporter une instance singleton
const apiUsageManager = new ApiUsageManager();
export default apiUsageManager;